import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import ApiService from '../services/api';
import './RequestDetail.css';

const RequestDetail = ({ theme, onToggleTheme }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [request, setRequest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formData, setFormData] = useState({});
  const [isEditing, setIsEditing] = useState(false);
  const [user, setUser] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [permissions, setPermissions] = useState({
    is_fad_user: false,
    is_fad_readonly: false,
    is_fad_supervisor: false,
    is_fad_admin: false,
    can_create_request_fad: false,
    can_upload_documents_fad: false,
    can_validate_documents: false,
    can_cancel_requests: false
  });
  const [settingPaymented, setSettingPaymented] = useState(false);
  const [returningToCapture, setReturningToCapture] = useState(false);
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [motivoValidacion, setMotivoValidacion] = useState('');
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState('');
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentPreviewUrl, setDocumentPreviewUrl] = useState('');
  const [documentPreviewLoading, setDocumentPreviewLoading] = useState(false);
  const [documentPreviewError, setDocumentPreviewError] = useState('');
  const [deletingDocumentKey, setDeletingDocumentKey] = useState(null);
  const [rejectingDocumentKey, setRejectingDocumentKey] = useState(null);
  const [showDocumentPreviewModal, setShowDocumentPreviewModal] = useState(false);
  const [showRejectDocumentModal, setShowRejectDocumentModal] = useState(false);
  const [rejectingTargetDoc, setRejectingTargetDoc] = useState(null);
  const [rejectingTargetIndex, setRejectingTargetIndex] = useState(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const [showCancelSolicitudModal, setShowCancelSolicitudModal] = useState(false);
  const [motivoCancelacion, setMotivoCancelacion] = useState('');
  const [cancellingSolicitud, setCancellingSolicitud] = useState(false);
  const [restoringSolicitud, setRestoringSolicitud] = useState(false);
  const [chatterMessages, setChatterMessages] = useState([]);
  const [chatterLoading, setChatterLoading] = useState(false);
  const [chatterError, setChatterError] = useState('');
  const [chatterBody, setChatterBody] = useState('');
  const [postingChatter, setPostingChatter] = useState(false);
  const [uploadingDocumentKey, setUploadingDocumentKey] = useState(null);
  const [uploadDocumentError, setUploadDocumentError] = useState('');
  const [showTomaUnidadModal, setShowTomaUnidadModal] = useState(false);
  const [tomaUnidadForm, setTomaUnidadForm] = useState({
    fecha_emision: new Date().toISOString().slice(0, 10),
    marca: '',
    modelo: '',
    monto: '',
    dueno: '',
  });
  const [generatingTomaUnidad, setGeneratingTomaUnidad] = useState(false);
  const [tomaUnidadError, setTomaUnidadError] = useState('');
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [signatureFiles, setSignatureFiles] = useState([]);
  const [signatureFilesLoading, setSignatureFilesLoading] = useState(false);
  const [signatureFilesError, setSignatureFilesError] = useState('');
  const [copyingLink, setCopyingLink] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [showPaymentConfirmModal, setShowPaymentConfirmModal] = useState(false);
  const [paymentConfirmForm, setPaymentConfirmForm] = useState({
    fecha_prog_pago: '',
    payment_details: '',
  });
  const [confirmingPayment, setConfirmingPayment] = useState(false);
  const [confirmPaymentError, setConfirmPaymentError] = useState('');

  const ineUploadInputRef = useRef(null);
  const estadoCuentaUploadInputRef = useRef(null);
  const poderNotarialUploadInputRef = useRef(null);
  const reciboPagoUploadInputRef = useRef(null);

  useEffect(() => {
    const userInfo = ApiService.getUserInfo();
    if (!userInfo) {
      navigate('/login');
      return;
    }
    setUser(userInfo);

    if (id) {
      loadRequest();
      loadPermissions();
    } else {
      navigate('/dashboard');
    }
  }, [id, navigate]);

  const handleLogout = () => {
    ApiService.logout();
    navigate('/login');
  };

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleClickOutside = (event) => {
    if (!event.target.closest('.user-menu')) {
      setShowUserMenu(false);
    }
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, []);

  const loadRequest = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getRefundRequest(parseInt(id));
      if (data) {
        setRequest(data);
        setFormData(data);
      } else {
        setError('Solicitud no encontrada');
      }
    } catch (error) {
      setError('Error al cargar la solicitud');
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPermissions = async () => {
    try {
      const response = await ApiService.getUserPermissions();
      if (response.status === 'success') {
        setPermissions(response.result);
      }
    } catch (error) {
      console.error('Error loading permissions:', error);
      // Si hay error, mantener permisos por defecto (sin permisos)
    }
  };

  const loadSignatureFiles = async (requisitionId) => {
    const rid = (requisitionId || '').trim();
    if (!rid) {
      setSignatureFiles([]);
      setSignatureFilesError('');
      return;
    }
    try {
      setSignatureFilesLoading(true);
      setSignatureFilesError('');
      const response = await ApiService.getResopSignatureFiles(rid);
      if (response?.status !== 'success') {
        setSignatureFiles([]);
        setSignatureFilesError(response?.error || 'No se pudieron cargar los documentos de firma');
        return;
      }

      const result = response?.result || {};
      const next = [];
      if (result.has_certified_pdf_2) {
        next.push({
          id: 'certified_pdf_2',
          nombre: 'Documento firmado (PDF)',
          type: 'pdf',
          source: 'request',
          docKey: 'certified_pdf_2',
        });
      }
      if (result.has_certified_pdf_1) {
        next.push({
          id: 'certified_pdf_1',
          nombre: 'Constancia de firma (PDF)',
          type: 'pdf',
          source: 'request',
          docKey: 'certified_pdf_1',
        });
      }

      const signers = Array.isArray(result.signers) ? result.signers : [];
      for (const signer of signers) {
        const signerLabel = (signer?.name || signer?.email || `Firmante ${signer?.id || ''}`).trim();
        if (signer?.has_video_acceptance) {
          next.push({
            id: `signer_${signer.id}_video_acceptance`,
            nombre: `Video lectura frase - ${signerLabel}`,
            type: 'video',
            source: 'signer',
            signerId: signer.id,
            docKey: 'video_acceptance',
          });
        }
        if (signer?.has_video_signature) {
          next.push({
            id: `signer_${signer.id}_video_signature`,
            nombre: `Video firma - ${signerLabel}`,
            type: 'video',
            source: 'signer',
            signerId: signer.id,
            docKey: 'video_signature',
          });
        }
      }

      setSignatureFiles(next);
    } catch (error) {
      console.error('Error cargando documentos de firma:', error);
      setSignatureFiles([]);
      setSignatureFilesError('Error al cargar los documentos de firma');
    } finally {
      setSignatureFilesLoading(false);
    }
  };

  const getDocumentKey = (doc, index = 0) => {
    const related = doc?.related_id === false ? 'false' : String(doc?.related_id ?? 'false');
    return `${doc?.document ?? 'unknown'}:${related}:${doc?.id ?? 'noid'}:${index}`;
  };

  const getDocumentLabel = (doc) => {
    if (!doc) return 'Documento';
    if (doc.document === 'identificacion') return 'Identificación';
    if (doc.document === 'estado_cuenta') return 'Estado de Cuenta';
    if (doc.document === 'poder_notarial') return 'Poder Notarial';
    if (doc.document === 'recibo_pago') return 'Recibo de Pago';
    if (doc.document === 'toma_unidad') return 'Toma de unidad';
    return 'Documento';
  };

  const getDocumentTypeLabel = (doc) => (doc?.type === 'image' ? 'Imagen' : 'Documento');

  const handleCloseDocumentPreviewModal = () => {
    setShowDocumentPreviewModal(false);
    setSelectedDocument(null);
    setDocumentPreviewUrl('');
    setDocumentPreviewError('');
    setDocumentPreviewLoading(false);
  };

  const handleOpenRejectDocumentModal = (doc, index) => {
    if (!doc) return;
    setError('');
    setRejectingTargetDoc(doc);
    setRejectingTargetIndex(index);
    setMotivoRechazo('');
    setShowRejectDocumentModal(true);
  };

  const handleCloseRejectDocumentModal = () => {
    setShowRejectDocumentModal(false);
    setRejectingTargetDoc(null);
    setRejectingTargetIndex(null);
    setMotivoRechazo('');
  };

  const handleOpenCancelSolicitudModal = () => {
    setError('');
    setMotivoCancelacion('');
    setShowCancelSolicitudModal(true);
  };

  const handleCloseCancelSolicitudModal = () => {
    setShowCancelSolicitudModal(false);
    setMotivoCancelacion('');
  };

  const handleCancelSolicitud = async () => {
    if (!motivoCancelacion.trim()) {
      setError('El motivo de cancelación es obligatorio');
      return;
    }
    try {
      setCancellingSolicitud(true);
      setError('');
      setSuccess('');
      await ApiService.cancelSolicitud(parseInt(id), motivoCancelacion.trim());
      setSuccess('Solicitud cancelada correctamente');
      handleCloseCancelSolicitudModal();
      await loadRequest();
    } catch (error) {
      console.error('Error cancelando solicitud:', error);
      setError('Error al cancelar la solicitud');
    } finally {
      setCancellingSolicitud(false);
    }
  };

  const handleRestoreSolicitudFromCancel = async () => {
    if (!window.confirm('¿Desea restablecer la solicitud a Draft?')) return;
    try {
      setRestoringSolicitud(true);
      setError('');
      setSuccess('');
      await ApiService.restoreSolicitudFromCancel(parseInt(id));
      setSuccess('Solicitud restaurada correctamente');
      await loadRequest();
    } catch (error) {
      console.error('Error restaurando solicitud:', error);
      setError('Error al restaurar la solicitud');
    } finally {
      setRestoringSolicitud(false);
    }
  };

  const handlePreviewDocument = async (doc) => {
    if (!doc) return;

    try {
      setShowDocumentPreviewModal(true);
      setDocumentPreviewLoading(true);
      setDocumentPreviewError('');
      setSelectedDocument(doc);

      const { blob } = await ApiService.getSolicitudDocumentContent(
        parseInt(id),
        doc.document,
        doc.related_id
      );

      const nextUrl = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      setDocumentPreviewUrl(nextUrl);
    } catch (error) {
      console.error('Error previsualizando documento:', error);
      setDocumentPreviewError('Error al cargar la previsualización del documento');
      setDocumentPreviewUrl('');
    } finally {
      setDocumentPreviewLoading(false);
    }
  };

  const handlePreviewSignatureFile = async (file) => {
    if (!file) return;
    const rid = (request?.requisitionId || '').trim();
    if (!rid) return;

    try {
      setShowDocumentPreviewModal(true);
      setDocumentPreviewLoading(true);
      setDocumentPreviewError('');
      setSelectedDocument({
        nombre: file.nombre,
        type: file.type === 'video' ? 'video' : file.type === 'pdf' ? 'pdf' : 'pdf',
      });

      let blob;
      if (file.source === 'request') {
        const res = await ApiService.getResopSignatureRequestFileContent(rid, file.docKey);
        blob = res?.blob;
      } else {
        const res = await ApiService.getResopSignatureSignerFileContent(rid, file.signerId, file.docKey);
        blob = res?.blob;
      }

      const nextUrl = URL.createObjectURL(blob instanceof Blob ? blob : new Blob([blob]));
      setDocumentPreviewUrl(nextUrl);
    } catch (error) {
      console.error('Error previsualizando documento de firma:', error);
      setDocumentPreviewError('Error al cargar la previsualización del documento de firma');
      setDocumentPreviewUrl('');
    } finally {
      setDocumentPreviewLoading(false);
    }
  };

  const handleDownloadDocument = () => {
    if (!documentPreviewUrl || !selectedDocument?.nombre) return;
    const anchor = document.createElement('a');
    anchor.href = documentPreviewUrl;
    anchor.download = selectedDocument.nombre;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
  };

  const loadDocuments = async () => {
    try {
      setDocumentsLoading(true);
      setDocumentsError('');

      const response = await ApiService.getSolicitudDocuments(parseInt(id));
      const docs = response?.docs || [];

      setDocuments(docs);
    } catch (error) {
      console.error('Error cargando documentos:', error);
      setDocumentsError('Error al cargar el listado de documentos');
      setDocuments([]);
    } finally {
      setDocumentsLoading(false);
    }
  };

  const loadChatter = async () => {
    try {
      setChatterLoading(true);
      setChatterError('');
      const response = await ApiService.getSolicitudChatter(parseInt(id));
      setChatterMessages(response?.messages || []);
    } catch (error) {
      console.error('Error cargando chatter:', error);
      setChatterError('Error al cargar el histórico de cambios');
      setChatterMessages([]);
    } finally {
      setChatterLoading(false);
    }
  };

  const handlePostChatter = async () => {
    if (!chatterBody.trim()) return;
    try {
      setPostingChatter(true);
      setChatterError('');
      await ApiService.postSolicitudChatterMessage(parseInt(id), chatterBody);
      setChatterBody('');
      await loadChatter();
    } catch (error) {
      console.error('Error publicando mensaje en chatter:', error);
      setChatterError('Error al enviar el mensaje');
    } finally {
      setPostingChatter(false);
    }
  };

  const handleDeleteDocument = async (doc, index) => {
    if (!doc) return;
    if (!window.confirm(`¿Eliminar el documento "${doc.nombre}"?`)) return;

    const key = getDocumentKey(doc, index);

    try {
      setDeletingDocumentKey(key);
      setError('');
      setSuccess('');

      const isSelected =
        selectedDocument?.document === doc.document &&
        ((selectedDocument?.related_id === false ? false : selectedDocument?.related_id) ===
          (doc.related_id === false ? false : doc.related_id));
      if (isSelected) {
        handleCloseDocumentPreviewModal();
      }

      await ApiService.deleteSolicitudDocument(parseInt(id), doc.document, doc.related_id);

      setSuccess('Documento eliminado correctamente');

      await loadDocuments();
      await loadRequest();
    } catch (error) {
      console.error('Error eliminando documento:', error);
      setError('Error al eliminar el documento');
    } finally {
      setDeletingDocumentKey(null);
    }
  };

  const handleRejectDocument = async () => {
    const doc = rejectingTargetDoc;
    const index = rejectingTargetIndex;
    if (!doc || index === null || index === undefined) return;
    if (!motivoRechazo.trim()) {
      setError('El motivo de rechazo es obligatorio');
      return;
    }

    const key = getDocumentKey(doc, index);
    const currentState = request?.state || '';
    let returnToDraft = currentState === 'onValidation';

    if (!returnToDraft && currentState && !['Draft', 'onValidation'].includes(currentState)) {
      returnToDraft = window.confirm(
        'La solicitud ya fue enviada a Firma. ¿Desea regresar el proceso a "En Progreso" (Draft) y limpiar datos de FAD?'
      );
    }

    try {
      setRejectingDocumentKey(key);
      setError('');
      setSuccess('');

      const isSelected =
        selectedDocument?.document === doc.document &&
        ((selectedDocument?.related_id === false ? false : selectedDocument?.related_id) ===
          (doc.related_id === false ? false : doc.related_id));
      if (isSelected) {
        handleCloseDocumentPreviewModal();
      }

      const documentLabel = getDocumentLabel(doc);
      const documentTypeLabel = getDocumentTypeLabel(doc);
      const documentName = doc?.nombre ? ` - ${doc.nombre}` : '';
      const documentoRechazado = `${documentLabel}${documentName} (${documentTypeLabel})`;

      await ApiService.rejectSolicitudDocument(
        parseInt(id),
        doc.document,
        doc.related_id,
        returnToDraft,
        motivoRechazo.trim(),
        documentoRechazado,
        documentTypeLabel
      );
      setSuccess('Documento rechazado correctamente');
      handleCloseRejectDocumentModal();

      await loadDocuments();
      await loadRequest();
    } catch (error) {
      console.error('Error rechazando documento:', error);
      setError('Error al rechazar el documento');
    } finally {
      setRejectingDocumentKey(null);
    }
  };

  const triggerUploadPicker = (inputRef) => {
    if (inputRef?.current) {
      inputRef.current.click();
    }
  };

  const uploadDocumentFiles = async (documentKey, files) => {
    if (!files || files.length === 0) return;
    try {
      setUploadDocumentError('');
      setError('');
      setSuccess('');
      setUploadingDocumentKey(documentKey);

      for (const file of files) {
        await ApiService.uploadSolicitudDocument(parseInt(id), documentKey, file, {
          montoEsperado: documentKey === 'recibo_pago' ? (formData.monto || request?.monto || '') : undefined,
        });
      }

      setSuccess('Documento(s) cargado(s) correctamente');
      await loadDocuments();
      await loadRequest();
    } catch (error) {
      console.error('Error cargando documento:', error);
      setUploadDocumentError('Error al cargar el documento');
    } finally {
      setUploadingDocumentKey(null);
    }
  };

  const handleUploadInputChange = async (documentKey, event) => {
    const files = Array.from(event?.target?.files || []);
    if (event?.target) {
      event.target.value = null;
    }
    await uploadDocumentFiles(documentKey, files);
  };

  const handleOpenTomaUnidadModal = () => {
    setTomaUnidadError('');
    setError('');
    setTomaUnidadForm({
      fecha_emision: new Date().toISOString().slice(0, 10),
      marca: '',
      modelo: '',
      monto: String(request?.monto ?? ''),
      dueno: '',
    });
    setShowTomaUnidadModal(true);
  };

  const handleCloseTomaUnidadModal = () => {
    setShowTomaUnidadModal(false);
    setTomaUnidadError('');
  };

  const handleTomaUnidadChange = (e) => {
    const { name, value } = e.target;
    setTomaUnidadForm(prev => ({ ...prev, [name]: value }));
    if (tomaUnidadError) setTomaUnidadError('');
  };

  const handleGenerateTomaUnidad = async () => {
    try {
      setGeneratingTomaUnidad(true);
      setTomaUnidadError('');
      setError('');
      setSuccess('');

      if (!tomaUnidadForm.marca.trim() || !tomaUnidadForm.modelo.trim() || !String(tomaUnidadForm.monto).trim()) {
        setTomaUnidadError('Marca, modelo y monto son obligatorios');
        return;
      }

      const payload = {
        fecha_emision: tomaUnidadForm.fecha_emision,
        marca: tomaUnidadForm.marca.trim(),
        modelo: tomaUnidadForm.modelo.trim(),
        monto: Number(tomaUnidadForm.monto),
        dueno: tomaUnidadForm.dueno.trim(),
      };

      await ApiService.createTomaUnidad(parseInt(id), payload);
      setSuccess('Toma de unidad generada correctamente');
      handleCloseTomaUnidadModal();
      await loadDocuments();
      await loadRequest();
    } catch (error) {
      console.error('Error generando toma de unidad:', error);
      setTomaUnidadError('Error al generar toma de unidad');
    } finally {
      setGeneratingTomaUnidad(false);
    }
  };

  useEffect(() => {
    if (documentPreviewUrl) {
      return () => URL.revokeObjectURL(documentPreviewUrl);
    }
  }, [documentPreviewUrl]);

  useEffect(() => {
    const canViewDocuments = permissions.is_fad_user || permissions.is_fad_readonly || permissions.is_fad_supervisor || permissions.can_upload_documents_fad || permissions.can_validate_documents;
    if (!id || !canViewDocuments) {
      setDocuments([]);
      setDocumentsError('');
      handleCloseDocumentPreviewModal();
      return;
    }
    loadDocuments();
  }, [id, permissions.is_fad_user, permissions.is_fad_readonly, permissions.is_fad_supervisor, permissions.can_upload_documents_fad, permissions.can_validate_documents]);

  useEffect(() => {
    const canViewSignature = !!permissions.can_validate_documents;
    const rid = (request?.requisitionId || '').trim();
    if (!id || !canViewSignature || !rid) {
      setSignatureFiles([]);
      setSignatureFilesError('');
      return;
    }
    loadSignatureFiles(rid);
  }, [id, permissions.can_validate_documents, request?.requisitionId]);

  useEffect(() => {
    if (!id || !permissions.is_fad_supervisor) {
      setChatterMessages([]);
      setChatterError('');
      setChatterBody('');
      return;
    }
    loadChatter();
  }, [id, permissions.is_fad_supervisor]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Limpiar mensajes cuando el usuario edite
    if (error) setError('');
    if (success) setSuccess('');
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError('');
      setSuccess('');

      await ApiService.updateRefundRequest(parseInt(id), formData);
      setSuccess('Solicitud guardada exitosamente');

      // Recargar los datos para reflejar los cambios
      await loadRequest();
    } catch (error) {
      setError('Error al guardar la solicitud');
      console.error('Error:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleSendToSignature = async () => {
    try {
      setSending(true);
      setError('');
      setSuccess('');

      // Primero guardar los cambios
      await ApiService.updateRefundRequest(parseInt(id), formData);

      // Luego enviar a firma
      await ApiService.sendToSignature(parseInt(id));

      setSuccess('Solicitud enviada a firma exitosamente');

      // Recargar los datos para reflejar el cambio de estado
      await loadRequest();
    } catch (error) {
      setError('Error al enviar la solicitud a firma');
      console.error('Error:', error);
    } finally {
      setSending(false);
    }
  };

  const signatureLink = (formData?.requisitionId || request?.requisitionId)
    ? `https://ia.cosit.com.mx/resop/signature/${formData?.requisitionId || request?.requisitionId}`
    : '';

  const handleCopyLink = async () => {
    try {
      setCopyingLink(true);
      setError('');
      setSuccess('');
      await navigator.clipboard.writeText(signatureLink);
      setSuccess('Enlace copiado al portapapeles');
      setTimeout(() => setSuccess(''), 2500);
    } catch (error) {
      // Fallback: usar el método antiguo
      try {
        const textArea = document.createElement('textarea');
        textArea.value = signatureLink;
        textArea.style.position = 'fixed';
        textArea.style.left = '-9999px';
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
        setSuccess('Enlace copiado al portapapeles');
        setTimeout(() => setSuccess(''), 2500);
      } catch (err) {
        setError('No se pudo copiar el enlace. Cópielo manualmente.');
        console.error('Error copiando enlace:', error, err);
      }
    } finally {
      setCopyingLink(false);
    }
  };

  const handleResendSignatureEmail = async () => {
    try {
      setResendingEmail(true);
      setError('');
      setSuccess('');
      await ApiService.resendSignatureEmail(parseInt(id));
      setSuccess('Correo reenviado exitosamente al cliente');
      // Recargar datos para actualizar chatter
      await loadRequest();
    } catch (error) {
      const message = error?.response?.data?.error || 'Error al reenviar el correo';
      setError(message);
      console.error('Error al reenviar correo:', error);
    } finally {
      setResendingEmail(false);
    }
  };

  // Determina si se debe mostrar la sección de firma (Sent o Pending + resop)
  const showSignatureInfo = (
    (request?.state === 'Sent' || request?.state === 'Pending') &&
    (request?.signature_provider_used === 'resop' || formData?.signature_provider_used === 'resop') &&
    signatureLink
  );

  // Función para abrir el modal de validación
  const handleOpenValidationModal = () => {
    setMotivoValidacion('');
    setShowValidationModal(true);
  };

  // Función para enviar a FAD con motivo de validación
  const handleSendToFadWithValidation = async () => {
    if (!motivoValidacion.trim()) {
      setError('El motivo de validación es obligatorio');
      return;
    }

    try {
      setSending(true);
      setError('');
      setSuccess('');
      setShowValidationModal(false);

      const response = await ApiService.sendToFadWithValidation(parseInt(id), motivoValidacion);

      setSuccess('Solicitud validada y enviada a FAD correctamente');

      // Recargar los datos para reflejar el cambio de estado
      await loadRequest();
    } catch (error) {
      setError('Error al validar y enviar a FAD');
      console.error('Error:', error);
    } finally {
      setSending(false);
    }
  };

  const handleSetPaymented = () => {
    handleOpenPaymentConfirmModal();
  };

  const handleReturnToCapture = async () => {
    if (!window.confirm('¿Desea regresar esta solicitud a captura?')) return;

    try {
      setReturningToCapture(true);
      setError('');
      setSuccess('');

      await ApiService.returnSolicitudToCapture(parseInt(id));
      setSuccess('La solicitud regresó a captura correctamente');
      await loadRequest();
    } catch (error) {
      setError('Error al regresar la solicitud a captura');
      console.error('Error:', error);
    } finally {
      setReturningToCapture(false);
    }
  };

  const handleTipoPagoChange = async (e) => {
    const newTipoPago = e.target.value;

    // Actualizar el formData inmediatamente para la UI
    setFormData(prev => ({
      ...prev,
      tipo_pago: newTipoPago
    }));

    // Si el usuario solo tiene permisos de readonly, enviar inmediatamente a la API
    if (permissions.is_fad_readonly && !permissions.is_fad_user) {
      try {
        setError('');
        setSuccess('');

        await ApiService.setTipoPago(parseInt(id), newTipoPago);
        setSuccess('Tipo de pago actualizado exitosamente');

        // Recargar los datos para reflejar el cambio
        await loadRequest();
      } catch (error) {
        setError('Error al actualizar el tipo de pago');
        console.error('Error:', error);

        // Revertir el cambio en caso de error
        setFormData(prev => ({
          ...prev,
          tipo_pago: request.tipo_pago || ''
        }));
      }
    }
    // Si es is_fad_user, el cambio se guardará con el resto del formulario
  };

  const handleFechaProgPagoChange = async (e) => {
    const newFecha = e.target.value;

    setFormData(prev => ({
      ...prev,
      fecha_prog_pago: newFecha || null
    }));

    if (canEditFechaProgPago && !isEditing) {
      try {
        setError('');
        setSuccess('');
        await ApiService.setFechaProgPago(parseInt(id), newFecha);
        setSuccess('Fecha programada de pago actualizada exitosamente');
        await loadRequest();
      } catch (error) {
        setError('Error al actualizar la fecha programada de pago');
        console.error('Error:', error);
        setFormData(prev => ({
          ...prev,
          fecha_prog_pago: request?.fecha_prog_pago || null
        }));
      }
    }
  };

  const handleOpenPaymentConfirmModal = () => {
    const defaultFecha = formData?.fecha_prog_pago || request?.fecha_prog_pago || '';
    setPaymentConfirmForm({
      fecha_prog_pago: defaultFecha ? String(defaultFecha).slice(0, 10) : '',
      payment_details: formData?.payment_details || request?.payment_details || '',
    });
    setConfirmPaymentError('');
    setShowPaymentConfirmModal(true);
  };

  const handlePaymentConfirmInputChange = (e) => {
    const { name, value } = e.target;
    setPaymentConfirmForm(prev => ({ ...prev, [name]: value }));
  };

  const handleConfirmPaymented = async () => {
    if (!paymentConfirmForm.fecha_prog_pago.trim()) {
      setConfirmPaymentError('Debes capturar la fecha programada de pago para continuar.');
      return;
    }
    try {
      setConfirmingPayment(true);
      setConfirmPaymentError('');
      setError('');
      setSuccess('');
      await ApiService.setStatePaymented(parseInt(id), {
        fecha_prog_pago: paymentConfirmForm.fecha_prog_pago,
        payment_details: paymentConfirmForm.payment_details,
      });
      setShowPaymentConfirmModal(false);
      setSuccess('Estado de la solicitud actualizado a "Pago generado" exitosamente');
      await loadRequest();
    } catch (error) {
      const message = error?.response?.data?.error || 'Error al actualizar el estado de la solicitud';
      setConfirmPaymentError(message);
      console.error('Error:', error);
    } finally {
      setConfirmingPayment(false);
    }
  };

  // Función para comparar nombres sin importar el orden
  const compareNames = (name1, name2) => {
    if (!name1 || !name2) return false;

    // Normalizar nombres: convertir a minúsculas, quitar acentos y espacios extra
    const normalize = (name) => {
      return name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '') // Quitar acentos
        .replace(/\s+/g, ' ') // Normalizar espacios
        .trim();
    };

    const normalizedName1 = normalize(name1);
    const normalizedName2 = normalize(name2);

    // Dividir en palabras y ordenar alfabéticamente
    const words1 = normalizedName1.split(' ').sort();
    const words2 = normalizedName2.split(' ').sort();

    // Comparar si tienen las mismas palabras
    return words1.length === words2.length &&
      words1.every((word, index) => word === words2[index]);
  };

  // Función para obtener mensajes informativos
  const getInfoMessages = () => {
    const messages = [];

    // Solo mostrar mensajes si está en estado Draft
    if (request?.state !== 'Draft') {
      return messages;
    }

    // 1. Verificar si los nombres no coinciden
    if (formData.nombre_completo_ine && formData.beneficiario) {
      if (!compareNames(formData.nombre_completo_ine, formData.beneficiario)) {
        messages.push({
          type: 'warning',
          text: 'El nombre del Beneficiario no corresponde a la persona que esta solicitando la devolución'
        });
      }
    }

    // 2. Verificar si el monto es mayor a 30,000
    if (formData.monto && parseFloat(formData.monto) > 30000) {
      messages.push({
        type: 'info',
        text: 'El monto solicitado excede el limite seguro, valide que el monto es correcto'
      });
    }

    // 3. Verificar campos faltantes
    const requiredFields = [
      { field: 'numero_cuenta', label: 'Número de Cuenta' },
      { field: 'clave_interbancaria', label: 'Clave Interbancaria' },
      { field: 'banco', label: 'Banco' },
      { field: 'numero_recibo', label: 'Número de Recibo' },
      { field: 'nombre_completo_ine', label: 'Nombre Completo INE' },
      { field: 'agencia', label: 'Agencia' },
      { field: 'client_phone', label: 'Teléfono del Cliente' }
    ];

    const missingFields = requiredFields.filter(({ field }) => !formData[field] || formData[field].toString().trim() === '');

    if (missingFields.length > 0) {
      const missingLabels = missingFields.map(({ label }) => label).join(', ');
      messages.push({
        type: 'info',
        text: `Se esta validando o esperando la información de "${missingLabels}" espere o valide con el cliente si la información ya fue enviada despues`
      });
    }

    return messages;
  };



  const getStateColor = (state) => {
    switch (state) {
      case 'Draft':
        return '#6c757d';
      case 'Sent':
        return '#007bff';
      case 'Pending':
        return '#ffc107';
      case 'Signed':
        return '#28a745';
      case 'Expired':
        return '#dc3545';
      case 'Cancelled':
        return '#6c757d';
      case 'Rejected':
      case 'Rejected_User':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  // Condiciones de permisos
  const canEdit = (request?.state === 'Draft' || request?.state === 'Rejected' || request?.state === 'Rejected_User') && permissions.is_fad_user;
  const canSendToSignature = request?.state === 'Draft' && permissions.is_fad_user;
  const canSetPaymented = request?.state === 'Signed' && (permissions.is_fad_user || permissions.is_fad_readonly);
  const canReturnToCapture = ['onValidation', 'Sent', 'Pending', 'Expired', 'Rejected'].includes(request?.state) &&
    (permissions.is_fad_user || permissions.is_fad_readonly);
  const canEditTipoPago = permissions.is_fad_user || permissions.is_fad_readonly;
  // Fecha programada de pago: visible/editable a partir del estado "Firmado" (Signed) o posterior
  const canShowFechaProgPago = ['Signed', 'Paymented', 'Expired', 'Cancelled', 'Rejected', 'Rejected_User'].includes(request?.state);
  const canEditFechaProgPago = request?.state === 'Signed' && (permissions.is_fad_user || permissions.is_fad_readonly);
  const canViewDocuments = permissions.is_fad_user || permissions.is_fad_readonly || permissions.is_fad_supervisor || permissions.can_upload_documents_fad || permissions.can_validate_documents;
  const isEditableDocumentState = request?.state === 'Draft' || request?.state === 'onValidation';
  const canUploadDocumentsNow = permissions.can_upload_documents_fad && isEditableDocumentState;
  const canDeleteDocuments = (permissions.is_fad_user || permissions.is_fad_readonly) && isEditableDocumentState;
  const canRejectDocuments = permissions.can_validate_documents && isEditableDocumentState;
  const canCancelSolicitud = permissions.can_cancel_requests && request?.state !== 'Cancelled';
  const canRestoreSolicitud = permissions.can_cancel_requests && request?.state === 'Cancelled';

  if (loading) {
    return (
      <div className="request-detail-container">
        <div className="loading">Cargando solicitud...</div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="request-detail-container">
        <div className="error">Solicitud no encontrada</div>
        <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
          Volver al Dashboard
        </button>
      </div>
    );
  }

  const statusDefs = [
    { key: 'Draft', label: 'En Progreso' },
    { key: 'onValidation', label: 'En espera de validación' },
    { key: 'Sent', label: 'Enviado a Firma' },
    { key: 'Pending', label: 'Pendiente de Firmar' },
    { key: 'Signed', label: 'Firmado' },
    { key: 'Paymented', label: 'Pagado' },
    { key: 'Expired', label: 'Expirado' },
    { key: 'Cancelled', label: 'Cancelado' },
    { key: 'Rejected', label: 'Rechazado' },
    { key: 'Rejected_User', label: 'Rechazado por Firmante' },
  ];

  const primaryStatusKeys = new Set(['Draft', 'onValidation', 'Sent', 'Pending', 'Signed', 'Paymented']);
  const activeStatusKey = request?.state || '';
  const activeIndex = Math.max(0, statusDefs.findIndex(s => s.key === activeStatusKey));
  const showInCollapsed = (def) => primaryStatusKeys.has(def.key);

  const collapsedBase = statusDefs.filter(showInCollapsed);
  const activeDef = statusDefs.find(s => s.key === activeStatusKey);
  let visibleStatuses = collapsedBase;
  if (!showAllStatuses) {
    if (activeDef && !collapsedBase.some(s => s.key === activeDef.key)) {
      const trimmed = collapsedBase.slice(0, Math.max(0, collapsedBase.length - 1));
      visibleStatuses = [...trimmed, activeDef];
    }
  } else {
    visibleStatuses = statusDefs;
  }

  const getPillVariant = (def) => {
    if (def.key === activeStatusKey) return 'active';
    const idx = statusDefs.findIndex(s => s.key === def.key);
    if (idx !== -1 && idx < activeIndex) return 'done';
    return 'muted';
  };

  return (
    <div className="request-detail-container">
      <header className="header">
        <div className="header-left">
          <div className="logo">
            COSIT - Devoluciones
          </div>
          <nav className="nav">
            <a href="#" onClick={() => navigate('/dashboard')} className="active">Dashboard</a>
            {/* <a href="#">Requests</a>
            <a href="#">Clients</a>
            <a href="#">Reports</a> */}
          </nav>
        </div>

        {/* <div className="header-center">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search"
              disabled
            />
          </div>
        </div> */}

        <div className="header-right">
          <button
            type="button"
            className="theme-toggle"
            onClick={onToggleTheme}
            aria-label={theme === 'dark' ? 'Cambiar a tema claro' : 'Cambiar a tema oscuro'}
            title={theme === 'dark' ? 'Tema claro' : 'Tema oscuro'}
          >
            {theme === 'dark' ? '☀' : '🌙'}
          </button>
          <div className="user-menu">
            <div className="user-profile" onClick={toggleUserMenu}>
              {user?.name?.charAt(0) || 'U'}
            </div>
            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-info">
                  <span className="user-name">{user?.name || 'Usuario'}</span>
                </div>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout-btn" onClick={handleLogout}>
                  Cerrar sesión
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <div className="statusbar">
        <div className="statusbar-pills" aria-label="Estatus de solicitud">
          {visibleStatuses.map((status) => (
            <div
              key={status.key}
              className={`statusbar-pill ${getPillVariant(status)}`}
              title={status.label}
            >
              <span className="statusbar-pill-label">{status.label}</span>
              {status.key === activeStatusKey && (
                <span className="statusbar-pill-badge">Actual</span>
              )}
            </div>
          ))}
          <button
            type="button"
            className="statusbar-toggle"
            onClick={() => setShowAllStatuses(prev => !prev)}
          >
            {showAllStatuses ? '<' : '>'}
          </button>
        </div>
      </div>

      <main className="main-content">
        <div className="page-header">
          <div className="page-title">
            <button
              onClick={() => navigate('/dashboard')}
              className="btn btn-secondary back-btn"
            >
              ← Volver
            </button>
            <h1>Solicitud de Devolución #{request.id}</h1>
          </div>
          <div style={{ display: 'flex', gap: '10px' }}>
            {canCancelSolicitud && (
              <button
                onClick={handleOpenCancelSolicitudModal}
                className="btn btn-warning"
                disabled={cancellingSolicitud}
              >
                {cancellingSolicitud ? 'Cancelando...' : 'Cancelar solicitud'}
              </button>
            )}
            {canEdit && !isEditing && permissions.is_fad_user && (
              <button
                onClick={() => setIsEditing(true)}
                className="btn btn-primary edit-btn"
              >
                Editar
              </button>
            )}
            {isEditing && permissions.is_fad_user && (
              <button
                onClick={() => setIsEditing(false)}
                className="btn btn-secondary cancel-btn"
              >
                Cancelar
              </button>
            )}
            {permissions.is_fad_readonly && ['Draft', 'onValidation'].includes(request?.state) && (
              <button
                onClick={handleOpenValidationModal}
                className="btn btn-success validate-btn"
                disabled={loading}
              >
                Validar y Enviar a FAD
              </button>
            )}
            {canReturnToCapture && (
              <button
                onClick={handleReturnToCapture}
                className="btn btn-secondary"
                disabled={returningToCapture}
              >
                {returningToCapture ? 'Regresando...' : 'Regresar a captura'}
              </button>
            )}
            {canRestoreSolicitud && (
              <button
                onClick={handleRestoreSolicitudFromCancel}
                className="btn btn-secondary"
                disabled={restoringSolicitud}
              >
                {restoringSolicitud ? 'Restableciendo...' : 'Restablecer a captura'}
              </button>
            )}
            
          </div>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        {success && (
          <div className="success-message">
            {success}
          </div>
        )}

        {request?.state === 'onValidation' && (
          <>
            <div className="info-message warning">
              <div className="info-text">
                <p>
                  En esta etapa debe validar la información completa de la solicitud antes de enviarla a Firma, es muy importante revisar todos los campos y documentos. ya que la información recibida fue marcada como información que puede ser falsa, montos incorrectos, él solicitante no coincide con los documentos proporcionados, los documentos no corresponden a los solicitados u otros motivos.
                </p>
                <br />
                <ul className="info-list" style={{ padding: '0 20px' }}>
                  <li>Verificar los campos de la solicitud.</li>
                  <li>Comprobar que los documentos proporcionados coinciden con los solicitados.</li>
                  <li>Revisar los montos y fechas de la solicitud.</li>
                  <li>Si encuentra cualquier problema o inquietud, por favor notifique al solicitante y cancele la solicitud.</li>
                  <li>Si toda la información es correcta, Regrese la solicitud a Captura y envie a Firma.</li>
                </ul>
              </div>
            </div>
          </>
        )}

        {/* Mensajes informativos para estado Draft */}
        {getInfoMessages().map((message, index) => (
          <div key={index} className={`info-message ${message.type}`}>
            <div className="info-icon">
              {message.type === 'warning' ? '⚠️' : 'ℹ️'}
            </div>
            <div className="info-text">
              {message.text}
            </div>
          </div>
        ))}

        {/* Sección informativa de firma (Solo para estados Sent/Pending y proveedor resop) */}
        {showSignatureInfo && (
          <div className="signature-info-card" style={{
            background: 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)',
            border: '1px solid #10b981',
            borderRadius: '12px',
            padding: '20px 24px',
            marginBottom: '24px',
            boxShadow: '0 4px 12px rgba(16, 185, 129, 0.12)'
          }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px' }}>
              <div style={{
                background: '#047857',
                color: '#ffffff',
                width: '44px',
                height: '44px',
                borderRadius: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                fontSize: '22px'
              }}>
                ✉️
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h3 style={{
                  margin: '0 0 6px 0',
                  fontSize: '16px',
                  fontWeight: 700,
                  color: '#064e3b'
                }}>
                  Solicitud en Proceso de Firma
                </h3>
                <p style={{
                  margin: '0 0 14px 0',
                  fontSize: '14px',
                  color: '#065f46',
                  lineHeight: 1.5
                }}>
                  La solicitud se encuentra actualmente en estado{' '}
                  <strong>
                    {request?.state === 'Sent' ? 'Enviado a Firma' : 'Pendiente de Firmar'}
                  </strong>
                  . Comparta el siguiente enlace con el cliente para que continúe con su proceso:
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px',
                  background: '#ffffff',
                  border: '1px solid #a7f3d0',
                  borderRadius: '8px',
                  padding: '8px 12px',
                  marginBottom: '14px',
                  flexWrap: 'wrap'
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
                    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
                  </svg>
                  <a
                    href={signatureLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      color: '#047857',
                      fontWeight: 600,
                      fontSize: '13px',
                      textDecoration: 'none',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      wordBreak: 'break-all'
                    }}
                  >
                    {signatureLink}
                  </a>
                </div>
                <div style={{
                  display: 'flex',
                  gap: '10px',
                  flexWrap: 'wrap'
                }}>
                  <button
                    type="button"
                    onClick={handleCopyLink}
                    disabled={copyingLink}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 18px',
                      background: '#ffffff',
                      color: '#047857',
                      border: '1.5px solid #10b981',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: copyingLink ? 'not-allowed' : 'pointer',
                      transition: 'all 0.15s ease',
                      opacity: copyingLink ? 0.7 : 1
                    }}
                    onMouseOver={(e) => {
                      if (!copyingLink) {
                        e.currentTarget.style.background = '#ecfdf5';
                      }
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.background = '#ffffff';
                    }}
                  >
                    {copyingLink ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                        Enlace copiado
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                          <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                        </svg>
                        Copiar enlace
                      </>
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={handleResendSignatureEmail}
                    disabled={resendingEmail}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      padding: '10px 18px',
                      background: resendingEmail ? '#059669' : '#047857',
                      color: '#ffffff',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: 600,
                      fontSize: '13px',
                      cursor: resendingEmail ? 'not-allowed' : 'pointer',
                      transition: 'background 0.15s ease',
                      boxShadow: '0 2px 6px rgba(4, 120, 87, 0.25)'
                    }}
                    onMouseOver={(e) => {
                      if (!resendingEmail) {
                        e.currentTarget.style.background = '#059669';
                      }
                    }}
                    onMouseOut={(e) => {
                      if (!resendingEmail) {
                        e.currentTarget.style.background = '#047857';
                      }
                    }}
                  >
                    {resendingEmail ? (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ animation: 'spin 0.8s linear infinite' }}>
                          <line x1="12" y1="2" x2="12" y2="6"></line>
                          <line x1="12" y1="18" x2="12" y2="22"></line>
                          <line x1="4.93" y1="4.93" x2="7.76" y2="7.76"></line>
                          <line x1="16.24" y1="16.24" x2="19.07" y2="19.07"></line>
                          <line x1="2" y1="12" x2="6" y2="12"></line>
                          <line x1="18" y1="12" x2="22" y2="12"></line>
                          <line x1="4.93" y1="19.07" x2="7.76" y2="16.24"></line>
                          <line x1="16.24" y1="7.76" x2="19.07" y2="4.93"></line>
                        </svg>
                        Enviando...
                      </>
                    ) : (
                      <>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                          <polyline points="22,6 12,13 2,6"></polyline>
                          <path d="M12 18v-6"></path>
                          <path d="M9 15l3 3 3-3"></path>
                        </svg>
                        Volver a enviar correo
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        <div className={`request-detail-layout ${permissions.is_fad_supervisor ? 'with-chatter' : 'no-chatter'}`}>
          <div className="request-detail-left">
            <div className="form-container">
              <form className="request-form">
            {/* Información del Cliente */}
            <div className="form-section">
              <h2>Información del Cliente</h2>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Nombre del Cliente *</label>
                  <input
                    type="text"
                    name="client_name"
                    value={formData.client_name || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Correo Electrónico *</label>
                  <input
                    type="email"
                    name="client_email"
                    value={formData.client_email || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Teléfono</label>
                  <input
                    type="tel"
                    name="client_phone"
                    value={formData.client_phone || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Cliente ID</label>
                  <input
                    type="text"
                    name="cliente_id"
                    value={formData.cliente_id || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={true}
                  />
                </div>
              </div>
            </div>

            {/* Información de la Solicitud */}
            <div className="form-section">
              <h2>Información de la Solicitud</h2>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fecha de Solicitud *</label>
                  <input
                    type="date"
                    name="fecha_solicitud"
                    value={formData.fecha_solicitud || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                    required
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Carpeta de Documentos</label>
                  <input
                    type="text"
                    name="carpeta_id"
                    value={formData.carpeta_id || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={true}
                  />
                  <a
                    href={`https://drive.google.com/drive/folders/${formData.carpeta_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="btn btn-primary"
                  >
                    Ver Carpeta
                  </a>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Agencia</label>
                  <input
                    type="text"
                    name="agencia"
                    value={formData.agencia || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Nombre Completo INE</label>
                  <input
                    type="text"
                    name="nombre_completo_ine"
                    value={formData.nombre_completo_ine || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Número INE</label>
                  <input
                    type="text"
                    name="numero_ine"
                    value={formData.numero_ine || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                    placeholder="Opcional"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">CURP</label>
                  <input
                    type="text"
                    name="curp"
                    value={formData.curp || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                    placeholder="Opcional"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Fecha de Nacimiento</label>
                  <input
                    type="date"
                    name="fecha_nacimiento"
                    value={formData.fecha_nacimiento ? formData.fecha_nacimiento.slice(0, 10) : ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Información Bancaria */}
            <div className="form-section">
              <h2>Información Bancaria</h2>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Número de Cuenta</label>
                  <input
                    type="text"
                    name="numero_cuenta"
                    value={formData.numero_cuenta || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Clave Interbancaria</label>
                  <input
                    type="text"
                    name="clave_interbancaria"
                    value={formData.clave_interbancaria || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Beneficiario</label>
                  <input
                    type="text"
                    name="beneficiario"
                    value={formData.beneficiario || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Banco</label>
                  <input
                    type="text"
                    name="banco"
                    value={formData.banco || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
              </div>
            </div>

            {/* Información del Pago */}
            <div className="form-section">
              <h2>Información del Pago</h2>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Número de Recibo</label>
                  <input
                    type="text"
                    name="numero_recibo"
                    value={formData.numero_recibo || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Fecha de Pago</label>
                  <input
                    type="date"
                    name="fecha_pago"
                    value={formData.fecha_pago || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Monto</label>
                  <input
                    type="number"
                    step="0.01"
                    name="monto"
                    value={formData.monto || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Emitido Por</label>
                  <input
                    type="text"
                    name="emitido_por"
                    value={formData.emitido_por || ''}
                    onChange={handleInputChange}
                    className="form-control"
                    disabled={!isEditing}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Tipo de Pago</label>
                  <select
                    name="tipo_pago"
                    value={formData.tipo_pago || 'Transferencia'}
                    onChange={handleTipoPagoChange}
                    className="form-control"
                    disabled={!isEditing && !canEditTipoPago}
                  >
                    <option value="Transferencia">Transferencia</option>
                    <option value="Efectivo">Efectivo</option>
                  </select>
                </div>
              </div>

              {canShowFechaProgPago && (
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">
                      Fecha de pago programada
                      {!canEditFechaProgPago && request?.state !== 'Paymented' && (
                        <span style={{ marginLeft: '8px', fontSize: '11px', color: '#6b7280', fontWeight: 400 }}>
                          (solo lectura)
                        </span>
                      )}
                    </label>
                    <input
                      type="date"
                      name="fecha_prog_pago"
                      value={formData.fecha_prog_pago ? String(formData.fecha_prog_pago).slice(0, 10) : ''}
                      onChange={canEditFechaProgPago && !isEditing ? handleFechaProgPagoChange : handleInputChange}
                      className="form-control"
                      disabled={!isEditing && !canEditFechaProgPago}
                    />
                    {canEditFechaProgPago && !isEditing && (
                      <div style={{ marginTop: '6px', fontSize: '12px', color: '#065f46', lineHeight: 1.4 }}>
                        Esta fecha se enviará resaltada al cliente junto con el monto en el correo de confirmación de pago.
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </form>

          {/* Botones de Acción */}
          {isEditing && permissions.is_fad_user && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                className="btn btn-primary"
                disabled={saving}
              >
                {saving ? 'Guardando...' : 'Guardar'}
              </button>

              {canSendToSignature && (
                <button
                  type="button"
                  onClick={handleSendToSignature}
                  className="btn btn-success"
                  disabled={sending || saving}
                >
                  {sending ? 'Enviando...' : 'Enviar a Firma'}
                </button>
              )}
            </div>
          )}

          {canViewDocuments && (
          <div className="documents-container" style={{marginBottom: '24px'}}>
            <div className="documents-header">
              <h2>Documentos</h2>
              {canUploadDocumentsNow && (
                <div className="documents-actions">
                  <button
                    type="button"
                    onClick={() => triggerUploadPicker(ineUploadInputRef)}
                    className="btn btn-secondary"
                    disabled={!!uploadingDocumentKey}
                  >
                    {uploadingDocumentKey === 'identificacion' ? 'Cargando...' : 'Cargar INE'}
                  </button>
                  <button
                    type="button"
                    onClick={() => triggerUploadPicker(estadoCuentaUploadInputRef)}
                    className="btn btn-secondary"
                    disabled={!!uploadingDocumentKey}
                  >
                    {uploadingDocumentKey === 'estado_cuenta' ? 'Cargando...' : 'Cargar Estado de Cuenta'}
                  </button>
                  <button
                    type="button"
                    onClick={() => triggerUploadPicker(poderNotarialUploadInputRef)}
                    className="btn btn-secondary"
                    disabled={!!uploadingDocumentKey}
                  >
                    {uploadingDocumentKey === 'poder_notarial' ? 'Cargando...' : 'Cargar Poder Notarial'}
                  </button>
                  <button
                    type="button"
                    onClick={() => triggerUploadPicker(reciboPagoUploadInputRef)}
                    className="btn btn-secondary"
                    disabled={!!uploadingDocumentKey}
                  >
                    {uploadingDocumentKey === 'recibo_pago' ? 'Cargando...' : 'Cargar Recibo de Pago'}
                  </button>
                  <button
                    type="button"
                    onClick={handleOpenTomaUnidadModal}
                    className="btn btn-secondary"
                    disabled={!!uploadingDocumentKey || generatingTomaUnidad}
                  >
                    {generatingTomaUnidad ? 'Generando...' : 'Generar Toma de Unidad'}
                  </button>
                  <input
                    ref={ineUploadInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadInputChange('identificacion', e)}
                  />
                  <input
                    ref={estadoCuentaUploadInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadInputChange('estado_cuenta', e)}
                  />
                  <input
                    ref={poderNotarialUploadInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadInputChange('poder_notarial', e)}
                  />
                  <input
                    ref={reciboPagoUploadInputRef}
                    type="file"
                    accept="application/pdf,image/*"
                    multiple
                    style={{ display: 'none' }}
                    onChange={(e) => handleUploadInputChange('recibo_pago', e)}
                  />
                </div>
              )}
            </div>

            {uploadDocumentError && (
              <div className="error-message">
                {uploadDocumentError}
              </div>
            )}

            {documentsError && (
              <div className="error-message">
                {documentsError}
              </div>
            )}

            <div className="documents-list">
              <div className="documents-list-header">
                <div>Documento</div>
                <div>Nombre</div>
                <div>Tipo</div>
                <div>Acciones</div>
              </div>
              {documentsLoading ? (
                <div className="documents-empty">
                  Cargando documentos...
                </div>
              ) : documents.length === 0 ? (
                <div className="documents-empty">
                  No hay documentos disponibles para esta solicitud.
                </div>
              ) : (
                documents.map((doc, index) => {
                  const key = getDocumentKey(doc, index);
                  const isSelected =
                    selectedDocument?.document === doc.document &&
                    ((selectedDocument?.related_id === false ? false : selectedDocument?.related_id) ===
                      (doc.related_id === false ? false : doc.related_id));

                  return (
                    <div
                      key={key}
                      className="documents-row"
                    >
                      <div className="documents-document">
                        {getDocumentLabel(doc)}
                      </div>
                      <div className="documents-name">
                        {doc.nombre}
                      </div>
                      <div className="documents-type">
                        {doc.type === 'image' ? 'Imagen' : 'Documento'}
                      </div>
                      <div className="documents-actions">
                        <button
                          type="button"
                          onClick={() => handlePreviewDocument(doc)}
                          className="btn btn-primary"
                          disabled={documentPreviewLoading && isSelected}
                        >
                          {documentPreviewLoading && isSelected ? 'Cargando...' : 'Previsualizar'}
                        </button>
                        {canRejectDocuments && (
                          <button
                            type="button"
                            onClick={() => handleOpenRejectDocumentModal(doc, index)}
                            className="btn btn-warning"
                            disabled={rejectingDocumentKey === key}
                          >
                            {rejectingDocumentKey === key ? 'Rechazando...' : 'Rechazar'}
                          </button>
                        )}
                        {canDeleteDocuments && (
                          <button
                            type="button"
                            onClick={() => handleDeleteDocument(doc, index)}
                            className="btn btn-danger"
                            disabled={deletingDocumentKey === key}
                          >
                            {deletingDocumentKey === key ? 'Eliminando...' : 'Eliminar'}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        )}

          {permissions.can_validate_documents && (
            <div className="documents-container" style={{ marginBottom: '24px' }}>
              <div className="documents-header">
                <h2>Documentos de firma</h2>
              </div>

              {!request?.requisitionId ? (
                <div className="documents-empty">
                  La solicitud aún no tiene un proceso de firma asociado.
                </div>
              ) : signatureFilesError ? (
                <div className="error-message">
                  {signatureFilesError}
                </div>
              ) : (
                <div className="documents-list">
                  <div className="documents-list-header">
                    <div>Archivo</div>
                    <div>Nombre</div>
                    <div>Tipo</div>
                    <div>Acciones</div>
                  </div>
                  {signatureFilesLoading ? (
                    <div className="documents-empty">
                      Cargando documentos de firma...
                    </div>
                  ) : signatureFiles.length === 0 ? (
                    <div className="documents-empty">
                      No hay documentos de firma disponibles.
                    </div>
                  ) : (
                    signatureFiles.map((file) => (
                      <div
                        key={file.id}
                        className="documents-row"
                      >
                        <div className="documents-document">
                          {file.type === 'video' ? 'Video' : 'Documento'}
                        </div>
                        <div className="documents-name">
                          {file.nombre}
                        </div>
                        <div className="documents-type">
                          {file.type === 'video' ? 'Video' : 'PDF'}
                        </div>
                        <div className="documents-actions">
                          <button
                            type="button"
                            onClick={() => handlePreviewSignatureFile(file)}
                            className="btn btn-primary"
                            disabled={documentPreviewLoading}
                          >
                            {documentPreviewLoading ? 'Cargando...' : 'Previsualizar'}
                          </button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          )}

          {/* Botón Enviar notificación de pago */}
          {canSetPaymented && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSetPaymented}
                className="btn btn-warning"
                disabled={settingPaymented}
              >
                {settingPaymented ? 'Enviando...' : 'Enviar notificación de pago'}
              </button>
            </div>
          )}
          
            </div>
          </div>

          {permissions.is_fad_supervisor && (
            <div className="request-detail-right">
              <div className="chatter-container">
                <div className="chatter-header">
                  <h2>Histórico de cambios</h2>
                  <button
                    type="button"
                    onClick={loadChatter}
                    className="btn btn-secondary"
                    disabled={chatterLoading}
                  >
                    {chatterLoading ? 'Cargando...' : 'Recargar'}
                  </button>
                </div>

                {chatterError && (
                  <div className="error-message">
                    {chatterError}
                  </div>
                )}

                <div className="chatter-messages">
                  {chatterLoading ? (
                    <div className="documents-empty">
                      Cargando histórico...
                    </div>
                  ) : chatterMessages.length === 0 ? (
                    <div className="documents-empty">
                      No hay mensajes para esta solicitud.
                    </div>
                  ) : (
                    chatterMessages.map((msg) => {
                      const dateText = msg?.date ? new Date(msg.date).toLocaleString('es-MX') : '';
                      return (
                        <div key={msg.id} className="chatter-message">
                          <div className="chatter-message-meta">
                            <div className="chatter-message-author">
                              {msg.author || 'Sistema'}
                            </div>
                            <div className="chatter-message-date">
                              {dateText}
                            </div>
                          </div>
                          <div
                            className="chatter-message-body"
                            dangerouslySetInnerHTML={{ __html: msg.body || '' }}
                          />
                        </div>
                      );
                    })
                  )}
                </div>

                <div className="chatter-compose">
                  <textarea
                    value={chatterBody}
                    onChange={(e) => setChatterBody(e.target.value)}
                    placeholder="Escribir mensaje..."
                    rows={3}
                    className="form-control"
                    disabled={postingChatter}
                  />
                  <button
                    type="button"
                    onClick={handlePostChatter}
                    className="btn btn-primary"
                    disabled={postingChatter || !chatterBody.trim()}
                  >
                    {postingChatter ? 'Enviando...' : 'Enviar'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        

        {showDocumentPreviewModal && (
          <div className="modal-overlay">
            <div className="doc-modal-container">
              <div className="doc-modal-header">
                <div className="doc-modal-title">
                  {selectedDocument?.nombre || 'Previsualización'}
                </div>
                <div className="doc-modal-actions">
                  <button
                    type="button"
                    onClick={handleDownloadDocument}
                    className="btn btn-success"
                    disabled={!documentPreviewUrl || documentPreviewLoading}
                  >
                    Descargar
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseDocumentPreviewModal}
                    className="btn btn-secondary"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
              <div className="doc-modal-body">
                {documentPreviewError ? (
                  <div className="documents-empty">
                    {documentPreviewError}
                  </div>
                ) : documentPreviewLoading ? (
                  <div className="documents-empty">
                    Cargando previsualización...
                  </div>
                ) : selectedDocument?.type === 'video' ? (
                  documentPreviewUrl ? (
                    <video
                      className="doc-preview-video"
                      controls
                      src={documentPreviewUrl}
                    />
                  ) : (
                    <div className="documents-empty">
                      No se pudo cargar el video.
                    </div>
                  )
                ) : selectedDocument?.type === 'image' ? (
                  documentPreviewUrl ? (
                    <img
                      src={documentPreviewUrl}
                      alt={selectedDocument?.nombre || 'Documento'}
                      className="doc-preview-image"
                    />
                  ) : (
                    <div className="documents-empty">
                      No se pudo cargar la imagen.
                    </div>
                  )
                ) : documentPreviewUrl ? (
                  <iframe
                    title={selectedDocument?.nombre || 'Documento'}
                    src={documentPreviewUrl}
                    className="doc-preview-iframe"
                  />
                ) : (
                  <div className="documents-empty">
                    No se pudo cargar el documento.
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showRejectDocumentModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <h3>Rechazar documento</h3>
              <p>
                Documento: <strong>{getDocumentLabel(rejectingTargetDoc)}</strong> ({getDocumentTypeLabel(rejectingTargetDoc)})
              </p>
              <textarea
                value={motivoRechazo}
                onChange={(e) => setMotivoRechazo(e.target.value)}
                placeholder="Motivo de rechazo (obligatorio)"
                rows={4}
                className="form-control"
                disabled={!!rejectingDocumentKey}
              />
              <div className="modal-buttons">
                <button
                  onClick={handleCloseRejectDocumentModal}
                  className="btn btn-secondary"
                  disabled={!!rejectingDocumentKey}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleRejectDocument}
                  className="btn btn-warning"
                  disabled={!motivoRechazo.trim() || !!rejectingDocumentKey}
                >
                  {rejectingDocumentKey ? 'Rechazando...' : 'Confirmar rechazo'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showCancelSolicitudModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <h3>Cancelar solicitud</h3>
              <p>Ingrese el motivo de cancelación:</p>
              <textarea
                value={motivoCancelacion}
                onChange={(e) => setMotivoCancelacion(e.target.value)}
                placeholder="Motivo de cancelación (obligatorio)"
                rows={4}
                className="form-control"
                disabled={cancellingSolicitud}
              />
              <div className="modal-buttons">
                <button
                  onClick={handleCloseCancelSolicitudModal}
                  className="btn btn-secondary"
                  disabled={cancellingSolicitud}
                >
                  Cancelar
                </button>
                <button
                  onClick={handleCancelSolicitud}
                  className="btn btn-warning"
                  disabled={cancellingSolicitud || !motivoCancelacion.trim()}
                >
                  {cancellingSolicitud ? 'Cancelando...' : 'Confirmar cancelación'}
                </button>
              </div>
            </div>
          </div>
        )}

        {showTomaUnidadModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <h3>Generar toma de unidad</h3>
              {tomaUnidadError && (
                <div className="error-message">
                  {tomaUnidadError}
                </div>
              )}
              <div className="modal-form">
                <div className="modal-row">
                  <label>Fecha de emisión</label>
                  <input
                    type="date"
                    name="fecha_emision"
                    value={tomaUnidadForm.fecha_emision}
                    onChange={handleTomaUnidadChange}
                    className="form-control"
                  />
                </div>
                <div className="modal-row">
                  <label>Marca *</label>
                  <input
                    type="text"
                    name="marca"
                    value={tomaUnidadForm.marca}
                    onChange={handleTomaUnidadChange}
                    className="form-control"
                  />
                </div>
                <div className="modal-row">
                  <label>Modelo *</label>
                  <input
                    type="text"
                    name="modelo"
                    value={tomaUnidadForm.modelo}
                    onChange={handleTomaUnidadChange}
                    className="form-control"
                  />
                </div>
                <div className="modal-row">
                  <label>Monto *</label>
                  <input
                    type="number"
                    step="0.01"
                    name="monto"
                    value={tomaUnidadForm.monto}
                    onChange={handleTomaUnidadChange}
                    className="form-control"
                  />
                </div>
                <div className="modal-row">
                  <label>Dueño</label>
                  <input
                    type="text"
                    name="dueno"
                    value={tomaUnidadForm.dueno}
                    onChange={handleTomaUnidadChange}
                    className="form-control"
                  />
                </div>
              </div>
              <div className="modal-buttons">
                <button
                  type="button"
                  onClick={handleCloseTomaUnidadModal}
                  className="btn btn-secondary"
                  disabled={generatingTomaUnidad}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleGenerateTomaUnidad}
                  className="btn btn-primary"
                  disabled={generatingTomaUnidad || !tomaUnidadForm.marca.trim() || !tomaUnidadForm.modelo.trim() || !String(tomaUnidadForm.monto).trim()}
                >
                  {generatingTomaUnidad ? 'Generando...' : 'Generar'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de validación */}
        {showValidationModal && (
          <div className="modal-overlay">
            <div className="modal-container">
              <h3>Validar y Enviar a FAD</h3>
              <p>Ingrese el motivo por el cual está validando esta solicitud:</p>
              <textarea
                value={motivoValidacion}
                onChange={(e) => setMotivoValidacion(e.target.value)}
                placeholder="Motivo de validación (obligatorio)"
                rows={4}
                className="form-control"
              />
              <div className="modal-buttons">
                <button
                  onClick={() => setShowValidationModal(false)}
                  className="btn btn-secondary"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleSendToFadWithValidation}
                  className="btn btn-primary"
                  disabled={!motivoValidacion.trim()}
                >
                  Confirmar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal de confirmación de Pago Generado */}
        {showPaymentConfirmModal && (
          <div className="modal-overlay" onClick={(e) => {
            if (!confirmingPayment && e.target.classList.contains('modal-overlay')) {
              setShowPaymentConfirmModal(false);
            }
          }}>
            <div className="modal-container" style={{ maxWidth: '560px' }}>
              <div style={{
                background: '#2c3e50',
                color: '#fff',
                margin: '-20px -24px 20px -24px',
                padding: '18px 24px',
                borderRadius: '10px 10px 0 0',
              }}>
                <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700 }}>
                  Confirmar generación de pago
                </h3>
                <div style={{ fontSize: '12px', opacity: 0.92, marginTop: '4px' }}>
                  Folio: <strong>#{request?.id}</strong> — Cliente: <strong>{request?.client_name || ''}</strong>
                </div>
              </div>

              <div style={{
                background: '#eff6ff',
                border: '1px solid #bfdbfe',
                borderRadius: '10px',
                padding: '14px 16px',
                marginBottom: '18px',
                fontSize: '13px',
                lineHeight: 1.5,
                color: '#1e3a8a',
              }}>
                <div style={{ fontWeight: 700, marginBottom: '6px' }}>ℹ️ Texto de ayuda</div>
                Al confirmar, el estado de la solicitud pasará a <strong>"Pagado"</strong> y se enviará un correo electrónico al cliente
                (<strong>{request?.client_email || ''}</strong>) donde se <u>resaltará visualmente</u>:
                <ul style={{ marginTop: '8px', marginBottom: 0, paddingLeft: '18px' }}>
                  <li>La <strong>fecha programada</strong> de su devolución.</li>
                  <li>El <strong>monto</strong> total a devolver.</li>
                  <li>Debajo, el texto libre que escribas en <strong>"Más información"</strong>.</li>
                </ul>
              </div>

              {confirmPaymentError && (
                <div className="error-message" style={{ marginBottom: '14px' }}>
                  {confirmPaymentError}
                </div>
              )}

              <div className="modal-form">
                <div className="modal-row">
                  <label style={{ fontWeight: 600, marginBottom: '6px' }}>
                    Fecha de pago programada <span style={{ color: '#dc2626' }}>*</span>
                  </label>
                  <input
                    type="date"
                    name="fecha_prog_pago"
                    value={paymentConfirmForm.fecha_prog_pago}
                    onChange={handlePaymentConfirmInputChange}
                    className="form-control"
                    disabled={confirmingPayment}
                  />
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563' }}>
                    Fecha en la que se espera el depósito se refleje en la cuenta del cliente.
                  </div>
                </div>

                <div className="modal-row">
                  <label style={{ fontWeight: 600, marginBottom: '6px' }}>
                    Agregar detalles <span style={{ color: '#6b7280', fontWeight: 400 }}>(opcional)</span>
                  </label>
                  <textarea
                    name="payment_details"
                    value={paymentConfirmForm.payment_details}
                    onChange={handlePaymentConfirmInputChange}
                    placeholder="Ej. El depósito se realizará por transferencia SPEI. Considere un día adicional por el fin de semana..."
                    rows={4}
                    className="form-control"
                    disabled={confirmingPayment}
                    style={{ resize: 'vertical', minHeight: '88px' }}
                  />
                  <div style={{ marginTop: '4px', fontSize: '11px', color: '#4b5563' }}>
                    Este texto aparecerá en el correo del cliente bajo la sección <strong>"Más información"</strong>.
                  </div>
                </div>
              </div>

              <div style={{
                marginTop: '16px',
                padding: '12px 14px',
                background: '#fafafa',
                border: '1px dashed #d1d5db',
                borderRadius: '8px',
                fontSize: '12px',
                color: '#374151',
              }}>
                <div style={{ fontWeight: 700, marginBottom: '4px' }}>Resumen del correo:</div>
                <div>Monto: <strong>${Number(request?.monto || 0).toLocaleString('es-MX', { minimumFractionDigits: 2 })}</strong></div>
                <div>Fecha programada: <strong>{paymentConfirmForm.fecha_prog_pago ? new Date(paymentConfirmForm.fecha_prog_pago + 'T00:00:00').toLocaleDateString('es-MX') : '(sin capturar)'}</strong></div>
              </div>

              <div className="modal-buttons" style={{ marginTop: '20px' }}>
                <button
                  type="button"
                  onClick={() => setShowPaymentConfirmModal(false)}
                  className="btn btn-secondary"
                  disabled={confirmingPayment}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleConfirmPaymented}
                  className="btn btn-warning"
                  disabled={confirmingPayment || !paymentConfirmForm.fecha_prog_pago.trim()}
                  style={{
                    background: '#d97706',
                    color: '#ffffff',
                    borderColor: '#d97706',
                    fontWeight: 600,
                  }}
                >
                  {confirmingPayment ? 'Procesando...' : 'Confirmar y enviar correo'}
                </button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default RequestDetail;
